// A flow letter's Klaviyo template (7.2 M5), as the hourly import left it:
// whether our copy could go out from us as it is ("kész") or still needs
// converting ("átírandó"), and an "Előnézet" that renders our copy as the
// country's letter — the sending account's footer, a sample contact, the
// trigger event's data as visible [[ … ]] placeholders — with the tags that
// still need work listed under it. Thin on purpose: one pill, one dialog.

import { createContext, useContext, useState } from "react";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { EyeIcon } from "lucide-react";
import { fetchFlowTemplate, fetchFlowTemplates, previewFlowTemplate } from "@/lib/api";
import type { FlowTemplateDetail, FlowTemplatePreview, FlowTemplateRow, FlowTemplateTag } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StatusPill } from "@/components/emc/StatusPill";
import { Callout, ErrorState, LoadingState } from "@/components/emc/states";
import { EMAIL_WIDTH } from "@/pages/templates/model";

/** The country's imported templates by Klaviyo id; null while loading or when
 * the list could not be read (the pills are then simply not shown). */
export function useFlowTemplates(country: string | null): Map<string, FlowTemplateRow> | null {
  const [state] = useLoad(
    () => (country ? fetchFlowTemplates(country) : Promise.resolve([])),
    [country],
  );
  if (state.data === null) return null;
  return new Map(state.data.map((row) => [row.template_source_id, row]));
}

interface Shared {
  country: string;
  rows: Map<string, FlowTemplateRow> | null;
}

const FlowTemplates = createContext<Shared | null>(null);

/** One list for every letter of a page: the outline has a dozen of them. */
export function FlowTemplatesProvider({ country, children }: { country: string; children: ReactNode }) {
  const rows = useFlowTemplates(country);
  return <FlowTemplates.Provider value={{ country, rows }}>{children}</FlowTemplates.Provider>;
}

/** The pills and the preview of a letter's Klaviyo template, from the page's
 * list; nothing outside a `FlowTemplatesProvider` or while it loads. */
export function KlaviyoTemplateOf({ sourceId }: { sourceId: string | null }) {
  const shared = useContext(FlowTemplates);
  if (shared === null || shared.rows === null || !sourceId) return null;
  return <KlaviyoTemplate country={shared.country} sourceId={sourceId} row={shared.rows.get(sourceId)} />;
}

const KIND_WORDS: Record<string, string> = {
  event: "esemény-adat",
  unsupported: "nem támogatott",
  contact: "kontakt-mező",
  system: "Klaviyo-rendszer",
};

/** The tags that keep the letter from going out, most frequent first. */
export function tagsNeedingWork(tags: FlowTemplateTag[]): FlowTemplateTag[] {
  return tags
    .filter((tag) => tag.kind === "event" || tag.kind === "unsupported")
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
}

export function KlaviyoTemplate({
  country,
  sourceId,
  row,
}: {
  country: string;
  sourceId: string;
  /** Undefined: the list has no row for it (not pulled yet). */
  row: FlowTemplateRow | undefined;
}) {
  const [open, setOpen] = useState(false);
  if (row === undefined) {
    return <span className="text-muted-foreground">még nincs importálva</span>;
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      {row.status !== "imported" ? (
        <StatusPill domain="flowTemplate" state={row.status} title={row.error ?? row.status} />
      ) : null}
      {row.conversion_status ? (
        <StatusPill domain="templateConversion" state={row.conversion_status} />
      ) : null}
      {row.version_id !== null && (
        <Button type="button" variant="link" size="sm" onClick={() => setOpen(true)}>
          <EyeIcon className="size-3.5" />
          Előnézet
        </Button>
      )}
      {open && <PreviewDialog country={country} sourceId={sourceId} row={row} onClose={() => setOpen(false)} />}
    </span>
  );
}

function PreviewDialog({
  country,
  sourceId,
  row,
  onClose,
}: {
  country: string;
  sourceId: string;
  row: FlowTemplateRow;
  onClose: () => void;
}) {
  const [detail] = useLoad<FlowTemplateDetail>(() => fetchFlowTemplate(country, sourceId), [country, sourceId]);
  const [preview] = useLoad<FlowTemplatePreview>(() => previewFlowTemplate(country, sourceId), [country, sourceId]);
  const work = detail.data ? tagsNeedingWork(detail.data.tags) : [];
  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent className="max-h-[calc(100vh-var(--emc-space-6))] max-w-[calc(var(--emc-panel-width)*1.6)] grid-rows-[auto_minmax(0,1fr)] overflow-hidden">
        <DialogHeader>
          <DialogTitle>Előnézet — {row.name ?? sourceId}</DialogTitle>
          <DialogDescription>
            A Klaviyo-sablon ({sourceId}) importált másolata, ahogy tőlünk menne ki: az ország feladójával és
            kötelező láblécével, egy minta-kontaktnak. A trigger-esemény adatai [[ … ]] jelöléssel látszanak.
            {row.template_key && row.email_template_id !== null && (
              <>
                {" "}
                Sablon:{" "}
                {/* The Sablonok gallery hides these copies by default; this is their way in. */}
                <Link to={`/sablonok/${row.email_template_id}`} className="text-blue-ink hover:underline">
                  {row.template_key}
                </Link>
                , v{row.version ?? "?"}.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex min-h-0 flex-col gap-3 overflow-y-auto">
          {detail.data?.problem && <Callout tone="warn">A motor kifogása: {detail.data.problem}</Callout>}
          {work.length > 0 && (
            <div className="rounded-card border border-line bg-surface-2 px-4 py-3 text-detail">
              <p className="m-0 mb-1.5 font-medium">Átírandó, mielőtt tőlünk kimehetne:</p>
              <ul className="m-0 flex list-none flex-col gap-1 p-0">
                {work.map((tag) => (
                  <li key={`${tag.kind}-${tag.tag}`}>
                    <span className="data-mono">{tag.tag}</span>
                    <span className="text-muted-foreground">
                      {" "}
                      × {tag.count} · {KIND_WORDS[tag.kind] ?? tag.kind}
                      {tag.reason ? ` · ${tag.reason}` : ""}
                    </span>
                  </li>
                ))}
              </ul>
              {(detail.data?.event_paths.length ?? 0) > 0 && (
                <p className="m-0 mt-2 text-muted-foreground">
                  Az esemény mezői, amiket olvas:{" "}
                  <span className="data-mono">{detail.data?.event_paths.join(", ")}</span>
                </p>
              )}
            </div>
          )}
          {detail.error && <ErrorState title="A sablon adatai nem olvashatók." message={detail.error} />}
          {preview.data === null && preview.error === null && <LoadingState lines={5} label="Az előnézet készül…" />}
          {preview.error && <ErrorState title="Az előnézet nem készült el." message={preview.error} />}
          {preview.data && (
            <>
              <p className="m-0">
                <span className="caps-label">Tárgy</span> <span className="font-medium">{preview.data.subject}</span>
              </p>
              {preview.data.warnings.map((warning) => (
                <Callout key={warning} tone="warn">
                  {warning}
                </Callout>
              ))}
              <div className="flex justify-center rounded-card border border-line bg-surface-2 p-4">
                <iframe
                  title={`Előnézet — ${sourceId}`}
                  sandbox=""
                  srcDoc={preview.data.html}
                  className="h-[60vh] max-w-full border-0 bg-surface shadow-emc"
                  style={{ width: EMAIL_WIDTH }}
                />
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
